import _ from "lodash";
import {useEffect, useState} from "react";
import {useAtomValue} from "jotai";
import {Loadable} from "jotai/vanilla/utils/loadable";
import {useTranslation} from "react-i18next";
import {Button, ListItemIcon, Menu, MenuItem, Stack, Typography} from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import MoreIcon from "@mui/icons-material/MoreVertOutlined";
import ArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import UploadIcon from "@mui/icons-material/FileUploadOutlined";
import {ResourceException} from "../../types/resourceError";
import {activeFieldLoadable} from "../../store/activeFieldStore";
import {Field} from "../../types/domain/field";
import {activeFieldResultsLoadable} from "../../store/activeFieldResultsStore";
import {Crop} from "../../types/domain/crop";
import {getLatestCropId} from "../../utils/fieldUtils";
import {DataDownloadDialog} from "../dialogs/DataDownloadDialog";
import {DataUploadDialog} from "../dialogs/DataUploadDialog";
import {routerCompanyIdAtom} from "../../store/routerStore";
import {useFields} from "../../hooks/fieldsHooks";
import {FieldIcon} from "../icons/FieldIcon";
import {EditIcon} from "../icons/EditIcon";
import {useCrops} from "../../hooks/cropsHooks";
import {useMeasureSystemUtils} from "../../hooks/measureSystemUtilsHooks";
import {getFieldAreaFormatted} from "../../utils/measureSystemUtils";
import {useAccessMarker} from "../../hooks/accessMarkerHooks";
import {ALL_ACCESS_FUNCTIONS} from "../../utils/accessControlUtils";
import {FieldEditDialog} from "../dialogs/FieldEditDialog";
import {useUserPreferences} from "../../hooks/userPreferencesHooks";
import dayjs from "dayjs";
import {formatShortDate} from "../../utils/dateUtils";

export const FieldHeader: React.FC = () => {
    const {t} = useTranslation();
    const access = useAccessMarker(ACCESS_FUNCTIONS);
    const msUtils = useMeasureSystemUtils();
    const companyId = useAtomValue(routerCompanyIdAtom);
    const {dateFormat} = useUserPreferences();
    const [lastThermalData, setLastThermalData] = useState<string>("");
    const [lastVisibleUpdate, setLastVisibleUpdate] = useState<string>("");
    const fieldState = useAtomValue(activeFieldLoadable);
    const field = fieldState.state === "hasData" && fieldState.data !== undefined ? fieldState.data : undefined;
    const {cropsById} = useCrops();
    const activeFieldResultsState = useAtomValue(activeFieldResultsLoadable);
    const [optionsAnchorEl, setOptionsAnchorEl] = useState<null | HTMLElement>(null);
    const [fieldEditDialogOpened, setFieldEditDialogOpened] = useState<boolean>(false);
    const [dataDownloadDialogOpened, setDataDownloadDialogOpened] = useState<boolean>(false);
    const [dataUploadDialogOpened, setDataUploadDialogOpened] = useState<boolean>(false);
    const fieldsState = useFields({companyId: companyId ?? null, orderId: undefined, farmId: undefined});

    useEffect(() => {
        if (activeFieldResultsState.state === "hasData") {
            _.forEachRight(activeFieldResultsState.data, (item) => {
                if (item.aggregatedProps.thermal_update !== 0) {
                    setLastThermalData(formatShortDate(dayjs(item.date), dateFormat));
                    return false;
                }
                return true;
            });
        }
    }, [setLastThermalData, activeFieldResultsState, dateFormat]);

    useEffect(() => {
        if (activeFieldResultsState.state === "hasData") {
            _.forEachRight(activeFieldResultsState.data, (item) => {
                if (item.aggregatedProps.visible_update !== 0) {
                    setLastVisibleUpdate(formatShortDate(dayjs(item.date), dateFormat));
                    return false;
                }
                return true;
            });
        }
    }, [setLastVisibleUpdate, activeFieldResultsState, dateFormat]);

    const onFieldEditDialogClose = (isSuccessful: boolean) => {
        setFieldEditDialogOpened(false);
        if (isSuccessful) {
            fieldsState.refresh();
        }
        setOptionsAnchorEl(null);
    };

    const onDataDownloadDialogClose = () => {
        setDataDownloadDialogOpened(false);
        setOptionsAnchorEl(null);
    };

    const onDataUploadDialogClose = () => {
        setDataUploadDialogOpened(false);
        setOptionsAnchorEl(null);
    };

    return (
        <Stack direction="column" gap="1rem">
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" gap="1.125rem">
                    {field && (
                        <FieldIcon
                            sx={{
                                width: "1.6rem",
                                height: "1.6rem",
                                stroke: "#5161C1",
                                strokeWidth: "2.2",
                            }}
                        />
                    )}
                    {fieldState.state === "hasError" && (
                        <ErrorIcon
                            sx={{
                                height: 36,
                                width: 36,
                            }}
                            color="error"
                        />
                    )}
                    <Typography fontSize={30} lineHeight={1.27} fontWeight={700}>
                        {renderTitle(fieldState)}
                    </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" gap="0.5rem">
                    <Button
                        startIcon={<MoreIcon />}
                        size="large"
                        onClick={(e) => setOptionsAnchorEl(e.currentTarget)}
                        endIcon={<ArrowDownIcon />}
                    >
                        {t("insights.field_header.options")}
                    </Button>
                    <Menu
                        id="field-options-menu"
                        anchorEl={optionsAnchorEl}
                        open={!!optionsAnchorEl}
                        onClose={() => setOptionsAnchorEl(null)}
                    >
                        {access.canEditCompanyFields && (
                            <MenuItem onClick={() => setFieldEditDialogOpened(true)}>
                                <ListItemIcon>
                                    <EditIcon
                                        sx={{
                                            stroke: "#8F94A8",
                                            width: "1.125rem",
                                            height: "1.125rem",
                                        }}
                                    />
                                </ListItemIcon>
                                {t("insights.field_header.edit")}
                            </MenuItem>
                        )}
                        <MenuItem onClick={() => setDataDownloadDialogOpened(true)}>
                            <ListItemIcon>
                                <DownloadIcon sx={{color: "#8F94A8"}} />
                            </ListItemIcon>
                            {t("insights.field_header.download")}
                        </MenuItem>
                        {access.canUploadCompanyData && (
                            <MenuItem onClick={() => setDataUploadDialogOpened(true)}>
                                <ListItemIcon>
                                    <UploadIcon sx={{color: "#8F94A8"}} />
                                </ListItemIcon>
                                {t("insights.field_header.add")}
                            </MenuItem>
                        )}
                    </Menu>
                </Stack>
            </Stack>
            <Stack direction="row" alignItems="center" gap="2rem">
                <Stack direction="row" gap="0.5rem">
                    <Typography textTransform="uppercase" color="#737990">
                        {t("insights.field_header.area")}
                    </Typography>
                    <Typography color="#3A3F4F">
                        {getFieldAreaFormatted(field, msUtils)} {msUtils.getAreaUnits()}
                    </Typography>
                </Stack>
                <Stack direction="row" gap="0.5rem">
                    <Typography textTransform="uppercase" color="#737990">
                        {t("insights.field_header.crop")}
                    </Typography>
                    <Typography textTransform="uppercase" color="#3A3F4F">
                        {renderCropName(getLatestCropId(field), cropsById) ?? "N/A"}
                    </Typography>
                </Stack>
                <Stack direction="row" gap="0.5rem">
                    <Typography textTransform="uppercase" color="#737990">
                        {t("insights.field_header.last_thermal_data")}
                    </Typography>
                    <Typography color="#3A3F4F">{lastThermalData}</Typography>
                </Stack>
                <Stack direction="row" gap="0.5rem">
                    <Typography textTransform="uppercase" color="#737990">
                        {t("insights.field_header.last_visible_update")}
                    </Typography>
                    <Typography color="#3A3F4F">{lastVisibleUpdate}</Typography>
                </Stack>
            </Stack>

            {fieldEditDialogOpened && companyId && field && (
                <FieldEditDialog
                    companyId={companyId}
                    selectedField={field}
                    open={fieldEditDialogOpened}
                    onClose={onFieldEditDialogClose}
                />
            )}

            {dataDownloadDialogOpened && companyId && (
                <DataDownloadDialog
                    open={dataDownloadDialogOpened}
                    mode="field"
                    onClose={onDataDownloadDialogClose}
                    companyId={companyId}
                    fieldId={field?.id}
                />
            )}

            {dataUploadDialogOpened && companyId && (
                <DataUploadDialog open={dataUploadDialogOpened} onClose={onDataUploadDialogClose} />
            )}
        </Stack>
    );
};

const renderTitle = (fieldState: Loadable<Promise<Field | undefined>>): React.ReactNode => {
    if (fieldState.state === "hasData" && fieldState.data) {
        return fieldState.data.name;
    } else if (fieldState.state === "hasError") {
        return (fieldState.error as ResourceException).resourceError.message;
    } else {
        return null;
    }
};

const renderCropName = (cropId: string | null | undefined, cropsById: Record<string, Crop> | undefined) => {
    return (cropId && cropsById?.[cropId].name)?.replace("_", " ");
};

const ACCESS_FUNCTIONS = _.pick(ALL_ACCESS_FUNCTIONS, ["canEditCompanyFields", "canUploadCompanyData"]);
